import { useLayoutEffect, type RefObject } from "react";
import { SPEED, framePaths, type RibbonWave } from "./ribbonWave";

type PathRef = RefObject<SVGPathElement | null>;

export type RibbonRefs = {
  /** The svg that is watched for being on screen. */
  svg: RefObject<SVGSVGElement | null>;
  /** The wide shadow line. */
  shadow: PathRef;
  /** The blurred main line. */
  soft: PathRef;
  /** The sharp tail. It gets the same d as the main line. */
  tail?: PathRef;
};

// About 30 fps: frames that come sooner are skipped.
export const FRAME_MS = 33;

const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/**
 * Moves a journey band with the wave engine. Draws straight on the paths with setAttribute, so no
 * frame renders React. Draws once at t = 0 and only runs the loop while motion is allowed, the band
 * is on screen and the tab is visible. With wave null it does nothing and the band stays as drawn.
 */
export const useRibbonWave = ({ svg, shadow, soft, tail }: RibbonRefs, wave: RibbonWave | null) => {
  useLayoutEffect(() => {
    const el = svg.current;
    if (!wave || !el) {
      return;
    }

    const draw = (t: number) => {
      const { main, shadow: back } = framePaths(wave, t);
      soft.current?.setAttribute("d", main);
      tail?.current?.setAttribute("d", main);
      shadow.current?.setAttribute("d", back);
    };

    // Without matchMedia or IntersectionObserver (jsdom, old browsers) motion is allowed and the band is on screen.
    const motion = typeof window.matchMedia === "function" ? window.matchMedia(REDUCED_MOTION) : null;
    let reduced = motion?.matches ?? false;
    let onScreen = true;
    let frame: number | null = null;
    let last: number | null = null;
    // Seconds the band has moved. It goes on from here after a pause, so the band does not jump.
    let elapsed = 0;

    const tick = (now: number) => {
      frame = requestAnimationFrame(tick);
      if (last === null) {
        last = now;
        return;
      }
      if (now - last < FRAME_MS) {
        return;
      }
      elapsed += (now - last) / 1000;
      last = now;
      draw(elapsed * SPEED);
    };

    const update = () => {
      const run = !reduced && onScreen && document.visibilityState !== "hidden";
      if (run && frame === null) {
        last = null;
        frame = requestAnimationFrame(tick);
      } else if (!run && frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    };

    const onMotion = (event: MediaQueryListEvent) => {
      reduced = event.matches;
      if (reduced) {
        elapsed = 0;
        draw(0);
      }
      update();
    };

    draw(0);
    motion?.addEventListener("change", onMotion);
    document.addEventListener("visibilitychange", update);
    const observer =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver((entries) => {
            onScreen = entries[entries.length - 1].isIntersecting;
            update();
          })
        : null;
    observer?.observe(el);
    update();

    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
      observer?.disconnect();
      motion?.removeEventListener("change", onMotion);
      document.removeEventListener("visibilitychange", update);
    };
  }, [wave, svg, shadow, soft, tail]);
};
